import { Observable, defer } from 'rxjs';
import { shareReplay } from 'rxjs/operators';
import { fetchAndResolve } from './fetch';

/**
 * Represents a single error report entry loaded from `error-reports-v2.json`.
 *
 * Error reports describe known error conditions, affected firmware versions,
 * error codes, and suggested remedies.
 */
export interface ErrorReport {
  /**
   * Logical grouping or category of the error (e.g. "Communication", "Safety").
   */
  group: string;

  /**
   * Short, human-readable error title or summary.
   */
  errorReport: string;

  /**
   * Detailed technical description of the error.
   */
  longForm: string;

  /**
   * High-level explanation intended for users or integrators.
   */
  explanation: string;

  /**
   * Raw list or formatted representation of associated error codes.
   */
  errorCodeList: string;

  /**
   * Recommended actions or steps to resolve the error.
   */
  remedy: string;

  /**
   * Indicates whether the error applies to firmware version 4.2.
   */
  'v4.2': boolean;

  /**
   * Indicates whether the error applies to firmware version 4.4.
   */
  'v4.4': boolean;

  /**
   * Indicates whether the error applies to firmware version 5.0.
   */
  'v5.0': boolean;

  /**
   * Indicates whether the error applies to firmware version 5.1.
   */
  'v5.1': boolean;

  /**
   * List of firmware identifiers where this error is known to occur.
   */
  firmwares: string[];

  /**
   * Numeric error code reported by the device or firmware.
   */
  errorCode: number;

  /**
   * Unique identifier for this error report entry.
   */
  id: string;
}

/**
 * Fetches the error reports JSON as an Observable.
 * Can optionally cache the result.
 *
 * @param useCache Whether to cache the result for future subscribers. Default: true.
 * @returns Observable of ErrorReport array
 */
export function getErrorReports(useCache = true): Observable<ErrorReport[]> {
  // Internal cache holder
  let cached$: Observable<ErrorReport[]> | undefined;

  // The actual getter
  const fetch$ = () => defer(() => fetchAndResolve('/error-reports-v2.json', 'json', false));

  if (!useCache) {
    return fetch$();
  }

  // Create cached observable if needed
  if (!cached$) {
    cached$ = fetch$().pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  return cached$;
}
